import React, { useCallback, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { USERS } from "@/constants/testIds";
import { useUsers } from "@/hooks/useUsers";
import { UserFilters } from "@/components/users/UserFilters";
import { BulkActions } from "@/components/users/BulkActions";
import { UserTable } from "@/components/users/UserTable";
import { UserDetailSheet } from "@/components/users/UserDetailSheet";

export default function Users() {
  const {
    users, templates, departments,
    q, setQ,
    department, setDepartment,
    statusFilter, setStatusFilter,
    loading, syncing, togglingStatus, bulkProcessing,
    load, doSync, toggleStatus, bulkUpdateStatus, updateLocalUser,
    templateNameOf,
  } = useUsers();

  // Selection & sheet state stay at page level because they orchestrate
  // multiple children (BulkActions bar + UserTable + UserDetailSheet).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [openUser, setOpenUser] = useState(null);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const visibleIds = users.map((u) => u.id);
    const allSelected = visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  }, [users, selectedIds]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handleBulk = useCallback(async (status) => {
    await bulkUpdateStatus(selectedIds, status);
    clearSelection();
  }, [bulkUpdateStatus, selectedIds, clearSelection]);

  const handleSheetUpdate = useCallback((userId, patch, opts) => {
    updateLocalUser(userId, patch);
    if (opts?.reload) load();
  }, [updateLocalUser, load]);

  const activeCount = users.filter((u) => u.status !== "disabled").length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-1">
            Directorio
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Usuarios</h1>
          <p className="text-sm text-slate-500 mt-1">
            {users.length} usuarios · {activeCount} activos · sincronizados desde Microsoft Graph.
          </p>
        </div>
        <Button
          onClick={doSync}
          disabled={syncing}
          className="bg-orange-600 hover:bg-orange-700 text-white transition-colors"
          data-testid={USERS.syncBtn}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.75} />
          {syncing ? "Sincronizando..." : "Sincronizar con Graph"}
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <BulkActions
          count={selectedIds.size}
          disabled={bulkProcessing}
          onActivate={() => handleBulk("active")}
          onDisable={() => handleBulk("disabled")}
          onClear={clearSelection}
        />
        <UserFilters
          q={q} onQChange={setQ}
          department={department} onDepartmentChange={setDepartment} departments={departments}
          statusFilter={statusFilter} onStatusChange={setStatusFilter}
        />
        <UserTable
          users={users}
          loading={loading}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelected}
          onToggleSelectAll={toggleSelectAll}
          onAssign={setOpenUser}
          onToggleStatus={toggleStatus}
          togglingStatus={togglingStatus}
          templateNameOf={templateNameOf}
        />
      </Card>

      <UserDetailSheet
        user={openUser}
        templates={templates}
        open={!!openUser}
        onClose={() => setOpenUser(null)}
        onUpdated={handleSheetUpdate}
      />
    </div>
  );
}
